import React, { useId } from 'react';
import { cn } from "@/lib/utils";

export type ArgusState = 'idle' | 'thinking' | 'speaking' | 'alert' | 'sleeping' | 'listening';
export type BlinkSpeed = 'slow' | 'normal' | 'fast' | 'none';

interface ArgusEyeProps {
  state?: ArgusState;
  blink?: BlinkSpeed;
  className?: string;
}

export const ArgusEye = ({ 
  state = 'idle', 
  blink = 'normal', 
  className,
}: ArgusEyeProps) => {
  const id = useId();
  
  const colors = {
    idle: "stroke-current",
    thinking: "stroke-blue-500",
    speaking: "stroke-purple-500",
    alert: "stroke-amber-500",
    sleeping: "stroke-slate-400",
    listening: "stroke-cyan-400"
  };

  const fillColors = {
    idle: "fill-current",
    thinking: "fill-blue-500",
    speaking: "fill-purple-500",
    alert: "fill-amber-500",
    sleeping: "fill-slate-400",
    listening: "fill-cyan-400"
  };

  const blinkDurations = {
    slow: "8s",
    normal: "4s",
    fast: "1.5s",
    none: "0s"
  };

  const rays = Array.from({ length: 8 }).map((_, i) => {
    const angle = (i * 45) * (Math.PI / 180);
    const x1 = 50 + Math.cos(angle) * 35;
    const y1 = 50 + Math.sin(angle) * 35;
    const x2 = 50 + Math.cos(angle) * 48;
    const y2 = 50 + Math.sin(angle) * 48;
    const isVisible = (state === 'idle' || state === 'sleeping') 
      ? (i >= 5 || i <= 7 || i === 0) 
      : true;
    return { x1, y1, x2, y2, isVisible };
  });

  const openPath = "M15 50 C15 50 35 25 50 25 C65 25 85 50 85 50 C85 50 65 75 50 75 C35 75 15 50 15 50 Z";
  const closedPath = "M15 50 C15 50 35 50 50 50 C65 50 85 50 85 50 C85 50 65 50 50 50 C35 50 15 50 15 50 Z";

  return (
    <div 
      className={cn(
        "relative flex items-center justify-center transition-all duration-700", 
        state === 'sleeping' && "animate-[pulse_4s_ease-in-out_infinite]",
        state === 'alert' && "animate-[bounce_0.2s_ease-in-out_infinite]",
        className
      )}
    >
      {/* Scanline Overlay for Alert state */}
      {state === 'alert' && (
        <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden rounded-full opacity-30">
          <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,0,0,0.5)_50%)] bg-[length:100%_4px] animate-[scan_2s_linear_infinite]" />
        </div>
      )}

      {/* Aura - Removed for better icon support in menu */}
      {/* <div className={cn(
        "absolute inset-0 rounded-full blur-2xl transition-all duration-1000",
        state === 'idle' && "bg-blue-500/10 animate-[aura-pulse_6s_ease-in-out_infinite]",
        state === 'thinking' && "bg-blue-500/20 scale-125",
        state === 'speaking' && "bg-purple-500/20 scale-125",
        state === 'alert' && "bg-amber-500/30 scale-150",
        state === 'listening' && "bg-cyan-500/20 scale-105",
        state === 'sleeping' && "bg-slate-500/5"
      )} /> */}

      <svg viewBox="0 0 100 100" className={cn("w-full h-full fill-none stroke-[3.5] stroke-linecap-round transition-colors duration-700", colors[state])}>
        <defs>
          <clipPath id={`clip-${id}`}>
            <path d={state === 'sleeping' ? closedPath : openPath}>
              {blink !== 'none' && state !== 'sleeping' && (
                <animate attributeName="d" values={`${openPath}; ${closedPath}; ${openPath}`} dur={blinkDurations[blink]} repeatCount="indefinite" keyTimes="0; 0.05; 0.1" />
              )}
            </path>
          </clipPath>
          
          <style>{`
            @keyframes spin360 {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes pupil-pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
            .animate-pupil-pulse {
              animation: pupil-pulse 1.5s ease-in-out infinite;
              transform-origin: 50% 50%;
            }
            @keyframes aura-pulse {
              50% { transform: scale(1.05); opacity: 0.7; }
            }
          `}</style>
        </defs>

        {/* Ray Rotation */}
        <g 
          className="transition-opacity duration-700" 
          style={{ 
            transformOrigin: '50% 50%',
            animation: (state === 'thinking' || state === 'speaking') ? 'spin360 3s linear infinite' : 'none'
          }}
        >
          {rays.map((ray, i) => (
            <line key={i} x1={ray.x1} y1={ray.y1} x2={ray.x2} y2={ray.y2} className={cn("transition-all duration-700", ray.isVisible ? "opacity-100" : "opacity-0")} />
          ))}
        </g>

        <g className={cn(state === 'alert' && "animate-[pulse_0.5s_ease-in-out_infinite]")}>
          <g clipPath={`url(#clip-${id})`}>
            {/* Group for idle animation */}
            <g>
              {state === 'idle' && (
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="-10 0; 10 0; -10 0"
                  dur="8s"
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0; 0.5; 1"
                  keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                />
              )}

              {/* Iris Rings (static part of inner eye) */}
              <circle cx="50" cy="50" r={state === 'listening' ? "18" : "14"} className="opacity-10 fill-current stroke-none transition-all duration-500" />
              <circle cx="50" cy="50" r={state === 'listening' ? "16" : "12"} className="stroke-[1.5] opacity-50 fill-none transition-all duration-500" />
              
              {/* Pupil Group - All animations are applied here */}
              <g style={{ transformOrigin: '50% 50%' }}>
                {state === 'idle' && (
                  <>
                    <animateTransform
                      attributeName="transform" type="scale"
                      values="1; 1.05; 1"
                      dur="5s"
                      repeatCount="indefinite"
                      additive="sum"
                    />
                  </>
                )}
                {(state === 'thinking' || state === 'speaking') && (
                  <>
                    <animateTransform
                      attributeName="transform" type="translate"
                      values="-5 0; 5 0; -5 0"
                      dur="3s"
                      repeatCount="indefinite"
                      additive="sum"
                    />
                    <animateTransform
                      attributeName="transform" type="scale"
                      values="1; 1.2; 1; 1.2; 1"
                      dur="2s"
                      repeatCount="indefinite"
                      additive="sum"
                    />
                  </>
                )}
                {state === 'alert' && (
                  <animateTransform
                    attributeName="transform" type="translate"
                    values="-2 -2; 2 2; -2 2; 2 -2; -2 -2"
                    dur="0.1s"
                    repeatCount="indefinite"
                  />
                )}

                {/* The actual pupil circles */}
                <g>
                  <circle 
                    cx="50" 
                    cy="50" 
                    r={state === 'alert' ? "8" : "6"} 
                    className={cn("fill-current stroke-none transition-all duration-500", fillColors[state])}
                  />
                  <circle cx="47" cy="47" r="2" fill="white" className="stroke-none opacity-80" />
                </g>
              </g>
            </g>
          </g>

          <path d={state === 'sleeping' ? closedPath : openPath} className="transition-all duration-500">
            {blink !== 'none' && state !== 'sleeping' && (
              <animate attributeName="d" values={`${openPath}; ${closedPath}; ${openPath}`} dur={blinkDurations[blink]} repeatCount="indefinite" keyTimes="0; 0.05; 0.1" />
            )}
          </path>
        </g>
      </svg>
    </div>
  );
};

export default ArgusEye;